/**
 * HandoffCard Component
 * Displays a single handoff with contact info, status, timer, and expandable activities
 */

import { FC, useState, useEffect } from 'react';
import { CheckCircle, XCircle, User, ChevronDown, ChevronUp, Loader2, History } from 'lucide-react';
import { Button } from '@crisellevaldez/speculo-ui';
import { formatDate } from '../../../../utils/dateTime';
import { signalFlagsFrom } from '../../../../utils/handoffFormat';
import { HandoffContactLine } from '../../handoff/HandoffContactLine';
import { HandoffMetaLine } from '../../handoff/HandoffMetaLine';
import { cn } from '../../../../utils/cn';
import {
  HandoffHistoryItem,
  HandoffDetailResponse,
  getHandoffDetailWithActivity,
} from '../../../../api/handoff';
import { getStatusStyle } from './handoffStyles';
import { getHotnessBadge, isKnownHotness } from '../../../../utils/handoffHotness';
import { ConversationSummaryCard } from './ConversationSummaryCard';
import { RecentActivitiesTable } from './RecentActivitiesTable';
import { HandoffHistoryTimeline } from './HandoffHistoryTimeline';
import { useOrganizationUsers } from '../../../../hooks/graphql/users/useOrganizationUsers';
import { CrmStageDisplay } from '../../../common/CrmStageDisplay';
import { useShowCrmStage } from '../../../../hooks/useShowCrmStage';
import { useHandoffCrmStages } from '../../../../hooks/useHandoffCrmStages';

export interface HandoffCardProps {
  handoff: HandoffHistoryItem;
  orgTimezone?: string;
  organizationId?: string;
  onClaim?: (handoffId: string) => Promise<void>;
  onDecline?: (handoffId: string) => Promise<void>;
  onContactClick?: (contact: { id: string; name: string; phone?: string; email?: string }) => void;
  isClaiming?: boolean;
  isDeclining?: boolean;
  processingId?: string | null;
}

export const HandoffCard: FC<HandoffCardProps> = ({
  handoff,
  orgTimezone,
  organizationId,
  onClaim,
  onDecline,
  onContactClick,
  isClaiming,
  isDeclining,
  processingId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [details, setDetails] = useState<HandoffDetailResponse | null>(null);

  // Lifecycle timeline (SPEC-2253) is on the handoff object already, so we can
  // show it immediately when present — open by default, but collapsible.
  // `created` is implied by the handoff existing, so drop it from the timeline.
  const timelineEvents = (handoff.history ?? []).filter((e) => e.event !== 'created');
  const hasHistory = timelineEvents.length > 0;
  const [showTimeline, setShowTimeline] = useState(false);
  // Resolve actor names for timeline events whose actorAgentName is missing —
  // same org-users lookup the assigned-agent cell uses. Only fetched for cards
  // that actually have a timeline.
  const { users: orgUsers } = useOrganizationUsers({ enabled: hasHistory });

  // Get hotness and status styles. The card uses the soft/translucent variant.
  const hotnessStyle = getHotnessBadge(handoff.hotnessLevel, 'soft');
  // Only show the hotness pill when the handoff is actually classified.
  // Unclassified handoffs (often expired ones) have no level, which would
  // otherwise render a meaningless "- · 0" placeholder.
  const hasHotness = isKnownHotness(handoff.hotnessLevel);
  // SPEC-2300: a row is pending for its whole 72-hour window and then
  // hard-expires, so the badge is a straight status lookup.
  const statusStyle = getStatusStyle(handoff.status);

  // Fetch details when expanded
  useEffect(() => {
    if (isExpanded && !details && organizationId) {
      setIsLoadingDetails(true);
      getHandoffDetailWithActivity(handoff.handoffId, organizationId)
        .then((data) => {
          setDetails(data);
        })
        .finally(() => {
          setIsLoadingDetails(false);
        });
    }
  }, [isExpanded, details, handoff.handoffId, organizationId]);

  // Get activity data from details response
  const hasActivityData = details?.contactActivity;
  const calls = details?.contactActivity?.calls || [];
  const emails = details?.contactActivity?.emails || [];
  const websiteActivity = details?.contactActivity?.websiteActivity || [];

  // Use conversation summary from details if available, otherwise from list
  const conversationSummary = details?.handoff?.conversationSummary || handoff.conversationSummary;
  const crmStages = useHandoffCrmStages(handoff.crmStages, details?.handoff?.crmStages);
  const showCrmStage = useShowCrmStage();

  // Signal flags that drove the hotness score, formatted for display.
  const signalChips = signalFlagsFrom(handoff.signals, handoff.reasons);

  return (
    <div className="border border-foreground/20 rounded-xl shadow-[0_3px_12px_-3px_rgba(0,0,0,0.11)] hover:shadow-[0_6px_18px_-4px_rgba(0,0,0,0.15)] hover:border-foreground/30 transition-all duration-150 overflow-hidden bg-card">
      {/* Card Header — neutral gradient, no divider (only the footer has a
          border). Grey rather than amber so the warm accents on the card —
          hotness pill, summary, lane icon — are the only colour competing for
          attention in a list of many cards. */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
              <button
                onClick={() =>
                  handoff.contactId &&
                  onContactClick?.({
                    id: handoff.contactId,
                    name: handoff.contactName || '',
                    phone: handoff.contactPhone || undefined,
                    email: handoff.contactEmail || undefined,
                  })
                }
                className="text-base sm:text-lg font-semibold text-foreground underline decoration-foreground/25 underline-offset-2 hover:text-primary hover:decoration-primary truncate max-w-[180px] sm:max-w-none text-left leading-tight"
              >
                {handoff.contactName || '-'}
              </button>
              {hasHotness && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
                    hotnessStyle.bg,
                    hotnessStyle.text
                  )}
                >
                  {hotnessStyle.icon}
                  <span>{hotnessStyle.label}</span>
                </span>
              )}
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
                  statusStyle.bg,
                  statusStyle.text
                )}
              >
                {statusStyle.label}
              </span>
            </div>
            <HandoffContactLine
              phone={handoff.contactPhone}
              email={handoff.contactEmail}
              location={handoff.location}
            />
            {showCrmStage && (
              <div className="mt-2 flex items-start gap-2">
                <span className="shrink-0 text-xs font-medium text-muted-foreground">CRM Stage</span>
                <CrmStageDisplay stages={crmStages} compact />
              </div>
            )}
            {/* Created date + provenance — under the contact info on mobile only */}
            <div className="mt-4 flex flex-col gap-1 sm:hidden">
              <span className="text-xs text-foreground/80 font-medium">
                {formatDate(handoff.createdAt, orgTimezone)}
              </span>
              <HandoffMetaLine channel={handoff.channelOfOrigin} />
            </div>
          </div>
          {/* Created date + provenance — on the right on desktop, centred
              against the taller contact block beside it. */}
          <div className="hidden shrink-0 self-center sm:flex sm:flex-col sm:items-end sm:gap-2">
            <span className="text-sm text-foreground/80 font-medium whitespace-nowrap">
              {formatDate(handoff.createdAt, orgTimezone)}
            </span>
            <HandoffMetaLine channel={handoff.channelOfOrigin} className="justify-end" />
          </div>
        </div>
      </div>

      {/* Card Body — only renders when there's actual content */}
      {(conversationSummary || signalChips.length > 0) && (
        <div className="px-5 py-4 space-y-4">
          {signalChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Signals
              </span>
              {signalChips.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center rounded-md border border-foreground/15 bg-foreground/[0.05] px-2 py-0.5 text-[11.5px] font-medium text-foreground/70"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
          {conversationSummary && <ConversationSummaryCard summary={conversationSummary} />}
        </div>
      )}

      {/* Card Footer */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-3 border-t border-foreground/10">
        <div className="flex items-center gap-2 text-sm">
          {handoff.status === 'pending' && onClaim && onDecline ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => onClaim(handoff.handoffId)}
                disabled={processingId === handoff.handoffId}
                className="bg-green-600 hover:bg-green-700 dark:bg-green-800 dark:hover:bg-green-700 dark:text-green-50 text-white h-8 px-3 text-xs"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                {processingId === handoff.handoffId && isClaiming ? 'Claiming…' : 'Claim'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDecline(handoff.handoffId)}
                disabled={processingId === handoff.handoffId}
                className="h-8 px-3 text-xs border-red-300 dark:border-red-700/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                {processingId === handoff.handoffId && isDeclining ? '…' : 'Decline'}
              </Button>
            </div>
          ) : null}
          {handoff.status === 'claimed' && handoff.assignedAgentName && (
            <span className="text-muted-foreground inline-flex items-center gap-1.5 flex-wrap">
              <User className="w-3 h-3 text-green-600 dark:text-green-400" />
              <span className="text-muted-foreground">Claimed by</span>
              <span className="text-foreground font-medium">{handoff.assignedAgentName}</span>
              {handoff.claimedAt && (
                <span className="text-muted-foreground hidden sm:inline">
                  · {formatDate(handoff.claimedAt, orgTimezone)}
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasHistory && (
            <button
              onClick={() => setShowTimeline((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="hidden sm:inline">
                {showTimeline ? 'Hide timeline' : 'Timeline'}
              </span>
              <span className="sm:hidden">Timeline</span>
              {showTimeline ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="hidden sm:inline">
              {isExpanded ? 'Hide activities' : 'View activities'}
            </span>
            <span className="sm:hidden">{isExpanded ? 'Hide' : 'Activities'}</span>
            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* Lifecycle timeline (SPEC-2253) content — toggled from the row above,
          open by default when the handoff has history. Independent of the
          activities accordion so it doesn't trigger the detail fetch. */}
      {hasHistory && showTimeline && (
        <div className="border-t border-foreground/10 bg-foreground/[0.02] px-3 py-3 sm:px-4 sm:py-4">
          <div className="mb-2 sm:mb-3 flex items-center gap-1.5">
            <History className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Timeline
            </span>
          </div>
          <HandoffHistoryTimeline
            events={timelineEvents}
            orgTimezone={orgTimezone}
            orgUsers={orgUsers}
          />
        </div>
      )}

      {/* Expanded Accordion Content - Recent Activities */}
      {isExpanded && (
        <div className="px-4 py-3 border-t border-foreground/10 bg-foreground/[0.02]">
          {isLoadingDetails ? (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground text-xs">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading activities…
            </div>
          ) : hasActivityData ? (
            <RecentActivitiesTable
              calls={calls}
              websiteActivity={websiteActivity}
              emails={emails}
              orgTimezone={orgTimezone}
            />
          ) : (
            <div className="py-4 text-center text-muted-foreground text-xs">
              No recent activities
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HandoffCard;
